import { ChangeEvent, FC, KeyboardEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { RenderIf } from 'components/RenderIf'
import { IconFull } from 'components/IconsContainers/styled'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { FiltersSlice } from 'featureSliced/shared/store/reducers/FiltersReducer'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'

import clickAndCollectLogo from 'assets/prompt/clickAndCollect.svg'
import qrMenuLogo from 'assets/prompt/qrMenu.svg'
import basket from 'assets/basket.svg'
import filters from 'assets/settings.svg'
import search from 'assets/search.svg'
import { ReactComponent as Burger } from 'assets/cover/burgerTwoColor.svg'

import {
  WebAction,
  WebActionLabel,
  WebActions,
  WebCartAction,
  WebHeaderBar,
  WebHeaderSpacer,
  WebLogo,
  WebLogoButton,
  WebSearch,
  WebSearchInput,
} from './styled'

type WebHeaderProps = {
  openAllergensModal: () => void
  openSidebar: () => void
  /** total of the local cart, already formatted; null hides the amount */
  cartTotal: string | null
  cartCount: number
  onCartClick: () => void
  /** the cart lives in its own column on wide screens, so the header drops it there */
  isCartInHeader: boolean
}

export const WebHeader: FC<WebHeaderProps> = ({
  openAllergensModal,
  openSidebar,
  cartTotal,
  cartCount,
  onCartClick,
  isCartInHeader,
}) => {
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const navigate = useNavigate()

  const { addSearch, discardSearch } = FiltersSlice.actions
  const storedSearch = useMySelector((state) => state.filters.search)
  const { logoImage } = useMySelector((state) => state.app.interfaceStyles)
  const { CompanyName } = useMySelector((state) => state.app.companyData)

  const [value, setValue] = useState(storedSearch || '')

  useEffect(() => {
    setValue(storedSearch || '')
  }, [storedSearch])

  const submit = () => {
    if (value) {
      dispatch(addSearch(value))
    } else {
      dispatch(discardSearch())
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') submit()
  }

  const fallbackLogo =
    process.env.REACT_APP_IS_CLIC === '1' ? clickAndCollectLogo : qrMenuLogo

  return (
    <WebHeaderBar data-testid="web-header">
      {/* back to the cover — the phone layout gets there with the browser back */}
      <WebLogoButton type="button" onClick={() => navigate('/')}>
        <WebLogo src={logoImage || fallbackLogo} alt={CompanyName} />
      </WebLogoButton>

      <WebSearch>
        <img src={search} alt="" width="18" height="18" />
        <WebSearchInput
          value={value}
          placeholder={t('footer.search')}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value)
          }
          onBlur={submit}
          onKeyDown={handleKeyDown}
          aria-label={t('footer.search')}
        />
      </WebSearch>

      <WebHeaderSpacer />

      <WebActions>
        <WebAction type="button" onClick={openAllergensModal}>
          <img src={filters} alt="" width="18" height="18" />
          <WebActionLabel>{t('footer.filters')}</WebActionLabel>
        </WebAction>

        <RenderIf condition={isCartInHeader}>
          <WebCartAction
            type="button"
            onClick={onCartClick}
            data-testid="web-cart-action"
          >
            <img src={basket} alt="" width="18" height="14" />
            <span>{convertToPrice(cartCount)}</span>
            <RenderIf condition={!!cartTotal}>
              <WebActionLabel>{cartTotal}</WebActionLabel>
            </RenderIf>
          </WebCartAction>
        </RenderIf>

        {/* the burger is always the last icon on the right — same mark as the cover
            header, the asset hard-codes a black fill so it needs the IconFull wrapper */}
        <WebAction
          type="button"
          onClick={openSidebar}
          aria-label={t('cover.menu')}
        >
          <IconFull fill="#fff" stroke="none">
            <Burger width="20" height="20" />
          </IconFull>
        </WebAction>
      </WebActions>
    </WebHeaderBar>
  )
}
